import { Preferences } from '@capacitor/preferences'
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core'
import { useThemeStore } from '@/stores/theme'

export type ThemeMode = 'light' | 'dark' | 'auto'

export interface ThemeManager {
    getCurrentTheme(): ThemeMode
    setTheme(theme: ThemeMode): Promise<void>
    applyTheme(theme?: ThemeMode): void
    detectSystemTheme(): 'light' | 'dark'
    isDarkMode(): boolean
    initialize(): Promise<void>
}

class ThemeManagerService implements ThemeManager {
    private readonly THEME_KEY = 'warden-theme'
    private readonly DEFAULT_THEME: ThemeMode = 'auto'
    private currentTheme: ThemeMode = 'auto'

    constructor() {
        // Apply a basic theme immediately to prevent flash
        // Full initialization happens via initialize() method
        if (typeof window !== 'undefined') {
            this.applyTheme(this.DEFAULT_THEME)
        }
    }

    /**
     * Public method to initialize theme from storage
     * Should be called during app startup
     */
    async initialize(): Promise<void> {
        await this.initializeTheme()
    }

    /**
     * Get the current theme mode
     */
    getCurrentTheme(): ThemeMode {
        try {
            const store = useThemeStore()
            return store.mode
        } catch (error) {
            // Fallback if store is not available
            return this.currentTheme
        }
    }

    /**
     * Set and persist the theme mode
     */
    async setTheme(theme: ThemeMode): Promise<void> {
        try {
            // Update internal state
            this.currentTheme = theme

            // Try to update store if available
            try {
                const store = useThemeStore()
                store.setMode(theme)
            } catch (storeError) {
                // Store not available, continue without it
                console.warn('Theme store not available:', storeError)
            }

            // Persist to storage
            await Preferences.set({
                key: this.THEME_KEY,
                value: theme
            })

            // Apply the theme immediately
            this.applyTheme(theme)
        } catch (error) {
            console.error('Failed to set theme:', error)
            throw error
        }
    }

    /**
     * Apply theme to the document using Ionic's recommended approach
     */
    applyTheme(theme?: ThemeMode): void {
        const targetTheme = theme || this.getCurrentTheme()
        this.currentTheme = targetTheme
        const isDark = this.shouldUseDarkMode(targetTheme)

        // Apply Ionic's .ion-palette-dark class to html element (recommended approach)
        document.documentElement.classList.toggle('ion-palette-dark', isDark)

        // Keep data-theme attribute for backward compatibility (but not the primary mechanism)
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')

        // Update body class for additional styling hooks
        document.body.classList.toggle('dark', isDark)
        document.body.classList.toggle('light', !isDark)

        // Sync system bars style with theme (edge-to-edge)
        // LIGHT style = light icons (for dark backgrounds)
        // DARK style = dark icons (for light backgrounds)
        this.updateSystemBarsStyle(isDark)
    }

    /**
     * Update system bars appearance to match the current theme.
     * DARK style = light/white icons (for dark backgrounds)
     * LIGHT style = dark/black icons (for light backgrounds)
     */
    private async updateSystemBarsStyle(isDark: boolean): Promise<void> {
        if (!Capacitor.isNativePlatform()) return

        try {
            await SystemBars.setStyle({
                style: isDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light
            })
        } catch (error) {
            console.warn('[ThemeManager] Failed to update system bars style:', error)
        }
    }

    /**
     * Detect system theme preference
     */
    detectSystemTheme(): 'light' | 'dark' {
        if (typeof window === 'undefined') return 'light'

        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }

    /**
     * Check if dark mode should be used
     */
    isDarkMode(): boolean {
        const currentTheme = this.getCurrentTheme()
        return this.shouldUseDarkMode(currentTheme)
    }

    /**
     * Determine if dark mode should be used based on theme mode
     */
    private shouldUseDarkMode(theme: ThemeMode): boolean {
        switch (theme) {
            case 'dark':
                return true
            case 'light':
                return false
            case 'auto':
            default:
                return this.detectSystemTheme() === 'dark'
        }
    }

    /**
     * Initialize theme from storage or defaults
     */
    private async initializeTheme(): Promise<void> {
        try {
            // Try to load theme from storage
            const result = await Preferences.get({ key: this.THEME_KEY })
            const storedTheme = result.value as ThemeMode

            if (storedTheme && ['light', 'dark', 'auto'].includes(storedTheme)) {
                this.currentTheme = storedTheme
                // Try to update store if available
                try {
                    const store = useThemeStore()
                    store.setMode(storedTheme)
                } catch (storeError) {
                    // Store not available, continue without it
                    console.warn('Theme store not available during initialization:', storeError)
                }
                this.applyTheme(storedTheme)
            } else {
                // Use default theme
                this.currentTheme = this.DEFAULT_THEME
                try {
                    const store = useThemeStore()
                    store.setMode(this.DEFAULT_THEME)
                } catch (storeError) {
                    // Store not available, continue without it
                    console.warn('Theme store not available during initialization:', storeError)
                }
                this.applyTheme(this.DEFAULT_THEME)
            }
        } catch (error) {
            console.error('Failed to initialize theme:', error)
            // Fallback to default theme
            this.currentTheme = this.DEFAULT_THEME
            try {
                const store = useThemeStore()
                store.setMode(this.DEFAULT_THEME)
            } catch (storeError) {
                // Store not available, continue without it
                console.warn('Theme store not available during fallback:', storeError)
            }
            this.applyTheme(this.DEFAULT_THEME)
        }
    }

    /**
     * Listen for system theme changes (for auto mode)
     */
    setupSystemThemeListener(): void {
        if (typeof window === 'undefined') return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        const handleChange = () => {
            const currentTheme = this.getCurrentTheme()
            if (currentTheme === 'auto') {
                this.applyTheme('auto')
            }
        }

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange)
        } else {
            // Legacy browsers
            mediaQuery.addListener(handleChange)
        }
    }

    /**
     * Remove system theme listener
     */
    removeSystemThemeListener(): void {
        if (typeof window === 'undefined') return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        if (mediaQuery.removeEventListener) {
            mediaQuery.removeEventListener('change', () => {})
        } else {
            mediaQuery.removeListener(() => {})
        }
    }
}

// Export singleton instance
export const themeManager = new ThemeManagerService()

// Export the service class for testing
export { ThemeManagerService }
